import type {
    AreaCharacteristic,
    Characteristic,
    CheckboxCharacteristic,
    CombiCharacteristic,
    Component,
    ExpressionCharacteristic,
    ListImageCharacteristic,
    MatrixValueCharacteristic,
    MultiQuantityCharacteristic,
    MultiselectCharacteristic,
    PackageCharacteristic,
    PriceListItem,
    ProductAttributeCharacteristic,
    RadioButtonsCharacteristic,
    SelectCharacteristic,
    TextCharacteristic,
    Value,
    ValuePriceOperator,
} from '../schema';
import type { CharacteristicSelection, SelectionsMap } from '../state/selections';
import type { EvaluatedMap } from '../eval/types';
import type { PriceLine } from './types';
import { effectiveValuePrice } from './choiceMatrix';

/**
 * Build the per-Characteristic price line for one Characteristic. Returns
 * an empty list when the user has made no selection or when the
 * characteristic doesn't contribute (webservice / UI-only types).
 *
 * `evaluated` is the result of {@link evaluateAll} — calculated types
 * (expression / combi / matrixvalue / productattribute) read their value
 * from there instead of recomputing.
 */
export function priceLinesFor(
    characteristic: Characteristic,
    selections: SelectionsMap,
    evaluated: EvaluatedMap,
    component: Component,
): PriceLine[] {
    const selection = selections[characteristic.id];

    switch (characteristic.type) {
        case 'select':
        case 'radiobuttons':
            return singlePickLine(characteristic, selection, component, evaluated);
        case 'multiselect':
        case 'checkbox':
            return multiPickLines(characteristic, selection, component, evaluated);
        case 'listimage':
            // listimage flips between single-pick and multi-pick at the
            // `isMultiple` flag — UI mirrors the same split. Route the
            // selection-kind accordingly so the picked value lands in the
            // summary + total in either mode.
            return characteristic.isMultiple
                ? multiPickLines(characteristic, selection, component, evaluated)
                : singlePickListImageLine(characteristic, selection, component, evaluated);
        case 'multiquantity':
            return quantityLines(characteristic, selection, component, evaluated);
        case 'text':
        case 'area':
            return freeEntryLine(characteristic, selection);
        case 'expression':
        case 'combi':
        case 'matrixvalue':
        case 'productattribute':
            return calculatedLine(characteristic, evaluated);
        case 'package':
            return packageLines(characteristic, selection);

        // Webservice / UI-only types (fileupload / chart / static / action
        // button / unknown) never contribute a price.
        default:
            return [];
    }
}

// ── Selection-based ─────────────────────────────────────────────────────────

function singlePickLine(
    characteristic: SelectCharacteristic | RadioButtonsCharacteristic,
    selection: CharacteristicSelection | undefined,
    component: Component,
    evaluated: EvaluatedMap,
): PriceLine[] {
    if (!selection || selection.kind !== 'single' || selection.valueId === null) return [];
    const value = characteristic.values.find((v) => v.id === selection.valueId);
    if (!value) return [];
    const amount = effectiveValuePrice(value, characteristic, component, evaluated) * pickQty(characteristic, selection.qty);
    return amount === 0
        ? [{ characteristic, value, amount: 0 }]
        : [{ characteristic, value, amount }];
}

function singlePickListImageLine(
    characteristic: ListImageCharacteristic,
    selection: CharacteristicSelection | undefined,
    component: Component,
    evaluated: EvaluatedMap,
): PriceLine[] {
    if (!selection || selection.kind !== 'single' || selection.valueId === null) return [];
    const value = characteristic.values.find((v) => v.id === selection.valueId);
    if (!value) return [];
    return [{
        characteristic,
        value,
        amount: effectiveValuePrice(value, characteristic, component, evaluated) * pickQty(characteristic, selection.qty),
    }];
}

function multiPickLines(
    characteristic:
        | MultiselectCharacteristic
        | CheckboxCharacteristic
        | ListImageCharacteristic,
    selection: CharacteristicSelection | undefined,
    component: Component,
    evaluated: EvaluatedMap,
): PriceLine[] {
    if (!selection || selection.kind !== 'multi') return [];
    return selection.valueIds
        .map((id) => characteristic.values.find((v) => v.id === id))
        .filter((v): v is Value => v !== undefined)
        .map((value) => ({
            characteristic,
            value,
            amount: effectiveValuePrice(value, characteristic, component, evaluated)
                * pickQty(characteristic, selection.qtys?.[value.id]),
        }));
}

/**
 * Quantity a picked Value's price is multiplied by (admin "Menge"). Only
 * when the Characteristic has a quantity mode; otherwise, and for an unset
 * invalid or zero quantity, 1 — legacy multiplies only when a quantity is
 * set (`if (this.getQty())`, option/choice.js).
 */
export function pickQty(
    characteristic: { quantityMode?: 0 | 1 | 2 },
    qty: number | undefined,
): number {
    if (!characteristic.quantityMode) return 1;
    return typeof qty === 'number' && Number.isFinite(qty) && qty > 0 ? qty : 1;
}

function quantityLines(
    characteristic: MultiQuantityCharacteristic,
    selection: CharacteristicSelection | undefined,
    component: Component,
    evaluated: EvaluatedMap,
): PriceLine[] {
    if (!selection || selection.kind !== 'quantities') return [];
    const lines: PriceLine[] = [];
    for (const [valueIdStr, qty] of Object.entries(selection.quantities)) {
        if (qty <= 0) continue;
        const value = characteristic.values.find((v) => v.id === Number(valueIdStr));
        if (!value) continue;
        const unit = effectiveValuePrice(value, characteristic, component, evaluated);
        lines.push({ characteristic, value, amount: unit * qty });
    }
    return lines;
}

// ── Free-entry (text/area) — pricelist-driven ──────────────────────────────

function freeEntryLine(
    characteristic: TextCharacteristic | AreaCharacteristic,
    selection: CharacteristicSelection | undefined,
): PriceLine[] {
    const numericInput =
        selection && selection.kind === 'text' ? Number(selection.value) : NaN;
    if (!Number.isFinite(numericInput)) return [];

    return pricelistContribution(
        characteristic,
        characteristic.pricelist,
        characteristic.operatorValuePrice,
        numericInput,
    );
}

// ── Calculated (expression / combi / matrixvalue / productattribute) ───────

function calculatedLine(
    characteristic:
        | ExpressionCharacteristic
        | CombiCharacteristic
        | MatrixValueCharacteristic
        | ProductAttributeCharacteristic,
    evaluated: EvaluatedMap,
): PriceLine[] {
    const value = evaluated.get(characteristic.id);
    // Non-numeric / unresolved → no contribution. (combi-string concat is
    // useful for labels but not for price arithmetic.)
    if (typeof value !== 'number' || !Number.isFinite(value)) return [];

    return pricelistContribution(
        characteristic,
        characteristic.pricelist,
        characteristic.operatorValuePrice,
        value,
    );
}

// ── Shared pricelist → contribution helper ─────────────────────────────────

function pricelistContribution(
    characteristic: Characteristic,
    pricelist: ReadonlyArray<PriceListItem>,
    operator: ValuePriceOperator,
    input: number,
): PriceLine[] {
    const matchedPrice = bestPricelistMatch(pricelist, input);
    if (matchedPrice === null) return [];

    const amount = operator === '*' ? matchedPrice * input : matchedPrice;
    if (amount === 0) return [];

    return [{ characteristic, value: undefined, amount }];
}

/**
 * Pick the matching pricelist tier that bounds the input most tightly:
 * for lower bounds (">", ">=") the highest threshold, for upper bounds
 * ("<", "<=") the lowest, "=" beats both; "!=" only wins when nothing
 * else matches. For the usual ">=" staffel this is "the highest tier at
 * or below the input". Ties keep the earlier tier. Returns null when no
 * tier matches.
 */
function bestPricelistMatch(
    pricelist: ReadonlyArray<PriceListItem>,
    input: number,
): number | null {
    let bestDistance = Infinity;
    let bestPrice: number | null = null;
    let matched = false;

    for (const item of pricelist) {
        const threshold = Number(item.value);
        if (!Number.isFinite(threshold)) continue;
        if (!operatorMatches(item.operator, input, threshold)) continue;
        const distance = boundDistance(item.operator, input, threshold);
        if (!matched || distance < bestDistance) {
            matched = true;
            bestDistance = distance;
            bestPrice = parsePrice(item.price);
        }
    }
    return bestPrice;
}

function boundDistance(op: string, input: number, threshold: number): number {
    switch (op) {
        case '=':
            return 0;
        case '>':
        case '>=':
            return input - threshold;
        case '<':
        case '<=':
            return threshold - input;
        default:
            return Infinity;
    }
}

function operatorMatches(op: string, lhs: number, rhs: number): boolean {
    switch (op) {
        case '=':
            return lhs === rhs;
        case '!=':
            return lhs !== rhs;
        case '>':
            return lhs > rhs;
        case '>=':
            return lhs >= rhs;
        case '<':
            return lhs < rhs;
        case '<=':
            return lhs <= rhs;
        default:
            return false;
    }
}

// ── Package ────────────────────────────────────────────────────────────────

function packageLines(
    characteristic: PackageCharacteristic,
    selection: CharacteristicSelection | undefined,
): PriceLine[] {
    // PackageView records "active" as `{ kind: 'single', valueId: 1 }`
    // (see applyPackage). Legacy charges the package price while the
    // package is active — the bundled values price themselves on their own
    // characteristics. Looking for a `multi` selection here meant an active
    // package never reached the total.
    const active =
        (selection?.kind === 'single' && selection.valueId !== null) ||
        (selection?.kind === 'multi' && selection.valueIds.length > 0);
    if (!active) return [];

    const packagePrice = parsePrice(characteristic.packagePrice);
    return packagePrice !== 0 ? [{ characteristic, value: undefined, amount: packagePrice }] : [];
}

// ── Helpers ────────────────────────────────────────────────────────────────

function parsePrice(raw: string | undefined): number {
    if (raw == null || raw === '') return 0;
    // Legacy quirk: prices can end in 'P' to indicate "percentage of running
    // total". Defer that semantic to the next phase — for now treat as plain
    // number, dropping the 'P' if present.
    const cleaned = raw.endsWith('P') ? raw.slice(0, -1) : raw;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
}
